/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import ExcelJS from 'exceljs'

/**************************************************************************************************/
/** @internal Type @name CellIndexerKey                                                          **/
/**************************************************************************************************/
type CellIndexerKey = string | number

/**************************************************************************************************/
/** @internal Type @name WorkSheetStates                                                         **/
/**************************************************************************************************/
type WorkSheetStates = 'visible' | 'hidden' | 'veryHidden'

/**************************************************************************************************/
/** @export Class @name ExcelSheet                                                               **/
/**************************************************************************************************/
export class ExcelSheet
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	private worksheet: ExcelJS.Worksheet

	/*************************************************/
	/** @Constructor                                **/
	/*************************************************/
	constructor(workbook: ExcelJS.Workbook, name: string)
	{
		this.worksheet = workbook.addWorksheet(name)
	}

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	private _cell(cellKey: ExcelJS.Cell | CellIndexerKey) : ExcelJS.Cell
	{
		if (typeof cellKey === 'string' || typeof cellKey === 'number') return this.worksheet.getCell(cellKey)
		return cellKey
	}

	public getCellText(cell: ExcelJS.Cell) : string
	{
		return cell.text
	}

	public getColumnCount(actualCount: boolean) : number
	{
		return actualCount
			? this.worksheet.actualColumnCount
			: this.worksheet.columnCount
	}

	public getLastRow() : ExcelJS.Row
	{
		return this.worksheet.lastRow
	}

	public getRowCount(actualCount: boolean) : number
	{
		return actualCount
			? this.worksheet.actualRowCount
			: this.worksheet.rowCount
	}

	public getRowLength(row: ExcelJS.Row) : number
	{
		return row.cellCount
	}

	public getRowNumValues(row: ExcelJS.Row) : number
	{
		return row.actualCellCount
	}

	public getWorkSheet() : ExcelJS.Worksheet
	{
		return this.worksheet
	}

	public setAutoFilter(filter: ExcelJS.AutoFilter) : this
	{
		this.worksheet.autoFilter = filter
		return this
	}

	public setCellAlignment(cellKey: ExcelJS.Cell | CellIndexerKey, h?: ExcelJS.Alignment['horizontal'], v?: ExcelJS.Alignment['vertical']) : this
	{
		const cell = this._cell(cellKey)
		const alignment = cell.alignment
		cell.alignment = { ...alignment, horizontal: h, vertical: v }
		return this
	}

	public setCellFontBold(cellKey: ExcelJS.Cell | CellIndexerKey, bold = true) : this
	{
		const cell = this._cell(cellKey)
		const font = cell.font
		cell.font = { ...font, bold }
		return this
	}

	public setCellFontColor(cellKey: ExcelJS.Cell | CellIndexerKey, color: string) : this
	{
		const cell = this._cell(cellKey)
		const font = cell.font
		cell.font = { ...font, color: { argb: color } }
		return this
	}

	public setCellSolidFill(cellKey: ExcelJS.Cell | CellIndexerKey, color: string) : this
	{
		const cell = this._cell(cellKey)
		cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } }
		return this
	}

	public setCellValue(cellKey: ExcelJS.Cell | CellIndexerKey, value: any) : this
	{
		const cell = this._cell(cellKey)
		cell.value = value
		return this
	}

	public setColumns(columns: Array<ExcelJS.Column>) : this
	{
		this.worksheet.columns = columns
		return this
	}

	public setColumnHeader(column: ExcelJS.Column, header: string) : this
	{
		column.header = header
		return this
	}

	public setColumnHidden(column: ExcelJS.Column, hide: boolean) : this
	{
		column.hidden = hide
		return this
	}

	public setColumnWidth(column: ExcelJS.Column, width: number) : this
	{
		column.width = width
		return this
	}

	public setRowCellValue(row: ExcelJS.Row, cellKey: CellIndexerKey, value: any) : this
	{
		row.getCell(cellKey).value = value
		return this
	}

	public setRowHeight(row: ExcelJS.Row, height: number) : this
	{
		row.height = height
		return this
	}

	public setRowHidden(row: ExcelJS.Row, hide: boolean) : this
	{
		row.hidden = hide
		return this
	}

	public setRowValuesFromArray(row: ExcelJS.Row, values: Array<any>) : this
	{
		row.values = values
		return this
	}

	public setRowValuesFromObject(row: ExcelJS.Row, values: { [key: string]: ExcelJS.CellValue }) : this
	{
		row.values = values
		return this
	}

	public setState(state: WorkSheetStates) : this
	{
		this.worksheet.state = state
		return this
	}
}